import React, { useState, useEffect } from 'react';
import axios from "../routers/axios";
import { tokenManager } from "../routers/api";
import { AlertTriangle } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadTestData } from '../components/test/utils/testLoader';

// Import components
import InstructionsScreen from '../components/test/InstructionsScreen';
import TestInterface from '../components/test/TestInterface';
import TabWarningModal from '../components/test/modals/TabWarningModal';
import FinalResultsModal from '../components/test/modals/FinalResultsModal';
import { areBothScenariosComplete, validateScenarioResults } from '../components/test/utils/completionUtils';
import useAntiCheat from '../hooks/useAntiCheat';
import useTimer from '../hooks/useTimer';
import ReadingResultsModal from "../components/test/modals/ReadingResultsModal";


const SBAULISSystem = () => {
  // ✅ Get testId from URL params
  const { testId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ State for dynamic test data
  const [testData, setTestData] = useState({
    id: null,
    test_name: "",
    description: "",
    scenarios: [],
    loading: true,
    error: null
  });


  const [currentStep, setCurrentStep] = useState('instructions');
  const savedScenario = localStorage.getItem(`activeScenarioId_${testId}`);
  const [currentScenario, setCurrentScenario] = useState(savedScenario ? Number(savedScenario) : 1);
  const [attemptId, setAttemptId] = useState(null);
  const viewMode = location.state?.viewMode || false;



  // ✅ Lưu lại scenario mỗi khi người dùng chuyển
  useEffect(() => {
    if (currentScenario) {
      localStorage.setItem(`activeScenarioId_${testId}`, currentScenario);
    }
  }, [currentScenario, testId]);



  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [showAIFeedback, setShowAIFeedback] = useState(false);
  const [writingAttempt, setWritingAttempt] = useState(1);
  const [readingFeedback, setReadingFeedback] = useState(null);
  const [readingQuestions, setReadingQuestions] = useState([]);
  const [writingText, setWritingText] = useState('');
  const [testAttemptData, setTestAttemptData] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);


  // State to save results from both scenarios
  const [scenario1Results, setScenario1Results] = useState(null);
  const [scenario2Results, setScenario2Results] = useState(null);

  const [progress, setProgress] = useState(0);


  // State to track completion of each scenario
  const [scenario1Completed, setScenario1Completed] = useState(false);
  const [scenario2Completed, setScenario2Completed] = useState(false);

  // 🔧 NEW: State to control FinalResultsModal display
  const [preventAutoFinalResults, setPreventAutoFinalResults] = useState(false);

  // Modal states
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showReadingResultsModal, setShowReadingResultsModal] = useState(false);
  const [loadedScenarios, setLoadedScenarios] = useState([]);

  const {
    tabSwitchCount,
    showTabWarning,
    warningLevel,
    isTestCancelled,
    setShowTabWarning
  } = useAntiCheat(currentStep, navigate);


  // ✅ Load test data + restore progress + handle timer persistence
  useEffect(() => {
    const fetchTest = async () => {
      if (!testId) {
        console.warn("⚠️ [fetchTest] testId không tồn tại (null/undefined)");
        setTestData((prev) => ({
          ...prev,
          loading: false,
          error: "No test ID",
        }));
        return;
      }

      try {
        setTestData((prev) => ({ ...prev, loading: true, error: null }));

        // 🟢 1️⃣ Lấy chi tiết bài test
        const url = `/test/detail/${testId}`;
        const res = await axios.get(url);
        const testDetail = res.data;

        // 👉 Tính tổng thời gian
        const totalDuration = (testDetail.scenarios || []).reduce(
          (sum, s) => sum + (s.duration_minutes || 0),
          0
        );

        setTestData({
          ...testDetail,
          totalDuration,
          loading: false,
          error: null,
        });

        // 🟢 2️⃣ Lấy dữ liệu attempt
        const user =
          JSON.parse(localStorage.getItem("user")) ||
          (window.tokenManager ? tokenManager.getUser() : null);

        if (!user?.id) {
          console.warn("⚠️ [fetchTest] Không tìm thấy user id để lấy attempt");
          return;
        }

        const attemptRes = await axios.get(`/test_attempts/${user.id}/${testId}`);
        console.log("🧾 [fetchTest] Attempt data:", attemptRes.data);
        setTestAttemptData(attemptRes.data);

        // 🧠 3️⃣ Map dữ liệu progress từ BE → FE
        if (attemptRes.data?.attempts?.length > 0) {
          const attempts = attemptRes.data.attempts;
          // ✅ FIX: dùng testDetail thay vì testData (stale React state sau setTestData)
          const s1 = attempts.find(a => a.scenario_id === testDetail.scenarios?.[0]?.id);
          const s2 = attempts.find(a => a.scenario_id === testDetail.scenarios?.[1]?.id);
          if (s1) {
            setScenario1Results({
              readingCompleted: !!s1.reading_submitted_at,
              writingCompleted: !!s1.writing_submitted_at,
              readingScore: s1.reading_score || 0,
              writingScore: s1.writing_score || 0,
            });
            if (s1.reading_submitted_at && s1.writing_submitted_at)
              setScenario1Completed(true);
          }

          if (s2) {
            setScenario2Results({
              readingCompleted: !!s2.reading_submitted_at,
              writingCompleted: !!s2.writing_submitted_at,
              readingScore: s2.reading_score || 0,
              writingScore: s2.writing_score || 0,
            });
            if (s2.reading_submitted_at && s2.writing_submitted_at)
              setScenario2Completed(true);
          }

          const completedCount =
            (s1?.reading_submitted_at ? 1 : 0) +
            (s1?.writing_submitted_at ? 1 : 0) +
            (s2?.reading_submitted_at ? 1 : 0) +
            (s2?.writing_submitted_at ? 1 : 0);

          const newProgress = (completedCount / 4) * 100;
          setProgress(newProgress);
          console.log(`📊 [Progress Restore] ${completedCount}/4 = ${newProgress}%`);
        }

        // 🕒 4️⃣ Kiểm tra điều kiện reset timer
        const attempts = attemptRes.data?.attempts || [];
        const hasProgress = attempts.some(
          (a) =>
            a.status === "in_progress" ||
            a.status === "completed" ||
            a.reading_submitted_at ||
            a.writing_submitted_at
        );

        if (!hasProgress) {
          console.log("🕒 [Timer] Test mới → reset timer");
          resetTimer();
        } else {
          console.log("⏱️ [Timer] Giữ nguyên thời gian từ localStorage");
        }
      } catch (err) {
        console.error("❌ [fetchTest] Lỗi khi gọi API:", err);

        if (err.response) {
          console.error("🔴 [fetchTest] Response lỗi:", err.response.status, err.response.data);
        } else if (err.request) {
          console.error("🔴 [fetchTest] Không nhận được response, request:", err.request);
        } else {
          console.error("🔴 [fetchTest] Lỗi khác:", err.message);
        }

        setTestData((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to load test",
        }));
      }
    };

    fetchTest();
  }, [testId]);



  // ✅ Get current scenario data dynamically
  const getCurrentScenarioData = () => {
    if (testData.loading || testData.error) return null;

    // 🔥 Tìm scenario theo ID, không dựa vào index
    const scenario = testData.scenarios?.find(s => s.id === currentScenario);

    return scenario || null;
  };


  const currentScenarioData = getCurrentScenarioData();

  const getActiveDuration = () => {
    const s = currentScenarioData;
    if (!s) return 0;

    if (currentStep === "reading") {
      return s.reading?.reading_duration || 0;
    }

    if (currentStep === "writing") {
      return s.writing?.estimated_time_minutes || 0;
    }

    return 0;
  };

  const activeDuration =
    currentStep === "reading"
      ? selectedScenario?.reading?.reading_duration || 0
      : currentStep === "writing"
        ? selectedScenario?.writing?.estimated_time_minutes || 0
        : 0;

  const remainingTimeFromDB =
    currentStep === "reading"
      ? (selectedScenario?.reading?.reading_duration || 0) * 60
      : currentStep === "writing"
        ? (selectedScenario?.writing?.estimated_time_minutes || 0) * 60
        : null;


  const { timeRemaining, formatTime, resetTimer } = useTimer(
    activeDuration,
    testId,
    remainingTimeFromDB,
    viewMode,
    null,
    currentScenario,
    currentStep
  );

  // 🔧 FIXED: Effect to check completion of both scenarios with better control
  useEffect(() => {
    // Only check when both scenarios are marked as completed
    // And not currently preventing auto final results
    if (scenario1Completed && scenario2Completed && !preventAutoFinalResults) {
      const bothScenariosComplete = areBothScenariosComplete(
        scenario1Results,
        scenario1Completed,
        scenario2Results,
        scenario2Completed
      );

      if (bothScenariosComplete && !viewMode) {
        console.log('Both scenarios fully completed — showing FinalResultsModal after delay');
        setTimeout(() => {
          setShowResultsModal(true);
        }, 500);
      } else if (viewMode) {
        console.log('👀 View mode detected — skipping FinalResultsModal');
      }

    }
  }, [scenario1Completed, scenario2Completed, scenario1Results, scenario2Results, preventAutoFinalResults]);

  // ✅ Function to mark scenario as completed
  const markScenarioComplete = (scenarioNumber, results) => {
    console.log(`🧩 [markScenarioComplete] called for scenario ${scenarioNumber}:`, results);

    // 🔒 1️⃣ Kiểm tra kết quả truyền vào
    if (!results) {
      console.warn(`⚠️ markScenarioComplete(${scenarioNumber}) called but results is empty, retrying...`);
      setTimeout(() => {
        if (scenarioNumber === 1 && scenario1Results) {
          markScenarioComplete(1, scenario1Results);
        } else if (scenarioNumber === 2 && scenario2Results) {
          markScenarioComplete(2, scenario2Results);
        }
      }, 200);
      return;
    }

    // 🔎 2️⃣ Kiểm tra hợp lệ cấu trúc dữ liệu
    const isValid = validateScenarioResults(results, scenarioNumber);
    if (!isValid) {
      console.warn(`⚠️ Scenario ${scenarioNumber}: Results structure not fully valid, continuing anyway`, results);
    }

    // 🧩 3️⃣ Chỉ cho phép mark complete khi CẢ reading + writing đều complete
    if (!results.readingCompleted || !results.writingCompleted) {
      console.warn(`🚫 Scenario ${scenarioNumber} chưa đủ reading + writing → chưa mark complete`);
      return;
    }

    // ✅ 4️⃣ Cập nhật theo từng scenario
    if (scenarioNumber === 1) {
      console.log('✅ Marking Scenario 1 complete');
      setScenario1Results(prev => ({
        ...(prev || {}),
        ...results,
        readingCompleted: true,
        writingCompleted: true,
      }));
      setScenario1Completed(true);
    } else if (scenarioNumber === 2) {
      console.log('✅ Marking Scenario 2 complete');
      setScenario2Results(prev => ({
        ...(prev || {}),
        ...results,
        readingCompleted: true,
        writingCompleted: true,
      }));
      setScenario2Completed(true);

      // ✅ 5️⃣ Khi scenario 2 xong, kiểm tra cả bài test
      const allDone = areBothScenariosComplete(
        scenario1Results,
        true,
        { ...results, readingCompleted: true, writingCompleted: true },
        true
      );

      if (allDone && !preventAutoFinalResults && !viewMode) {
        console.log('🎯 Cả 2 scenario hoàn thành — hiển thị FinalResultsModal sau 1 giây');
        setTimeout(() => setShowResultsModal(true), 1000);
      } else if (viewMode) {
        console.log('👀 View mode detected — skipping FinalResultsModal');
      } else {
        console.log('⌛ Chưa đủ điều kiện hiển thị FinalResultsModal');
      }

    }
  };



  // 🔧 NEW: Function to allow FinalResultsModal display (called from WritingFeedback)
  const allowFinalResults = () => {
    console.log('allowFinalResults called - enabling final results modal');
    setPreventAutoFinalResults(false);

    // Check if conditions are met to show modal
    if (!viewMode && scenario1Completed && scenario2Completed) {
      setTimeout(() => {
        setShowResultsModal(true);
      }, 300);
    }

  };

  const isCurrentScenarioComplete = () => {
    if (currentScenario === testData.scenarios[0]?.id) {
      return scenario1Completed && scenario1Results?.readingCompleted && scenario1Results?.writingCompleted;
    }
    else if (currentScenario === testData.scenarios[1]?.id) {
      return scenario2Completed && scenario2Results?.readingCompleted && scenario2Results?.writingCompleted;
    }
    return false;
  };

  // ✅ Loading state
  if (testData.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Loading Test {testId}...
          </h2>
          <p className="text-gray-600">Please wait a moment</p>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (testData.error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Test Loading Error</h2>
          <p className="text-gray-600 mb-6">{testData.error}</p>
          <div className="space-x-4">
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/student/dashboard')}
              className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main render logic
  const renderCurrentScreen = () => {
    if (isTestCancelled) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Test Cancelled</h2>
            <p className="text-gray-600 mb-4">You have switched tabs too many times.</p>
            <button
              onClick={() => navigate('/student/dashboard')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      );
    }

    switch (currentStep) {
      case 'instructions':
        return (
          <InstructionsScreen
            onStartTest={(step, scenarioId, selectedScenarioData, viewMode) => {
              console.log("🧭 Start Test Flow:", { step, scenarioId, selectedScenarioData });
              const storedScenario = localStorage.getItem("activeScenarioId");
              setLoadedScenarios(selectedScenarioData ? [selectedScenarioData] : []);
              setSelectedScenario(selectedScenarioData); // ✅ Lưu scenario được chọn
              setCurrentScenario(scenarioId || Number(storedScenario) || 1);
              setCurrentStep(step || 'reading');
            }}
            testId={testId}
            testName={testData?.test_name || `Test ${testId}`}
            scenarios={testData?.scenarios || []}
            totalDuration={testData?.totalDuration || 0}   // 👉 thêm dòng này
            resetTimer={resetTimer}   // ✅ truyền xuống đây
            testAttemptData={testAttemptData}   // ✅ thêm dòng này
          />
        );
      case 'reading':
      case 'writing':
        return (
          <TestInterface
            testData={testData}     // ✅ thêm dòng này
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            currentScenario={currentScenario}
            setCurrentScenario={setCurrentScenario}
            currentScenarioData={currentScenarioData}
            timeRemaining={timeRemaining}
            formatTime={formatTime}
            answers={answers}
            setAnswers={setAnswers}
            readingFeedback={readingFeedback}
            setReadingFeedback={setReadingFeedback}
            writingText={writingText}
            setWritingText={setWritingText}
            writingAttempt={writingAttempt}
            setWritingAttempt={setWritingAttempt}
            submitted={submitted}
            setSubmitted={setSubmitted}
            showAIFeedback={showAIFeedback}
            setShowAIFeedback={setShowAIFeedback}
            scenario1Results={scenario1Results}
            setScenario1Results={setScenario1Results}
            scenario2Results={scenario2Results}
            setScenario2Results={setScenario2Results}
            showResultsModal={showResultsModal}
            setShowResultsModal={setShowResultsModal}
            showReadingResultsModal={showReadingResultsModal}
            setShowReadingResultsModal={setShowReadingResultsModal}
            navigate={navigate}
            markScenarioComplete={markScenarioComplete}
            isCurrentScenarioComplete={isCurrentScenarioComplete}
            scenario1Completed={scenario1Completed}
            scenario2Completed={scenario2Completed}
            testId={testId}
            // 🔧 NEW: Add prop to control final results
            allowFinalResults={allowFinalResults}
            preventAutoFinalResults={preventAutoFinalResults}
            setReadingQuestions={setReadingQuestions}   // ✅ thêm dòng này
            loadedScenarios={loadedScenarios}   // ✅ truyền vào
            viewMode={viewMode} // ✅ thêm dòng này

          // selectedScenario={selectedScenario}
          />
        );
      default:
        return (
          <InstructionsScreen
            onStartTest={() => setCurrentStep('reading')}
            testId={testId}
            testName={location.state?.testData?.name || `Test ${testId}`}
          />
        );
    }
  };

  return (
    <div className="font-sans antialiased">
      {renderCurrentScreen()}
      <TabWarningModal
        showTabWarning={showTabWarning}
        warningLevel={warningLevel}
        setShowTabWarning={setShowTabWarning}
      />
      <FinalResultsModal
        showResultsModal={showResultsModal}
        setShowResultsModal={setShowResultsModal}
        scenario1Results={scenario1Results}
        scenario2Results={scenario2Results}
        navigate={navigate}
        testId={testId}                  // ✅ truyền trực tiếp như Reading/Writing
        testName={location.state?.testData?.name || `Test ${testId}`}
      />
      {/* ✅ Reading Results Modal - xuất hiện sau khi nhấn Final Submit Reading */}
      {showReadingResultsModal && (
        <ReadingResultsModal
          attemptId={attemptId}
          setAttemptId={setAttemptId}
          showReadingResultsModal={showReadingResultsModal}
          setShowReadingResultsModal={setShowReadingResultsModal}
          readingFeedback={readingFeedback}
          currentScenario={currentScenario}
          currentTest={testId}
          setCurrentStep={setCurrentStep}
          questions={readingQuestions}   // ✅ truyền đúng dữ liệu thật
        />
      )}

    </div>
  );
};

export default SBAULISSystem;